import Auth from "../auth"
import { defer, from, Observable } from "rxjs"
import { ajax } from "rxjs/ajax"
import { filter, map, switchMap } from "rxjs/operators"
import { GetUploadLinksResponseInfo } from "../protocol/drive_pb"
import { RESEND_ACTIVATION_EMAIL } from "../constants/urls"

// tslint:disable:no-console
const getToken$ = defer(() =>
    from(Auth.currentSession()).pipe(
        map((session) => session.getIdToken().getJwtToken()),
    ),
)

const authHeader$ = defer(() =>
    getToken$.pipe(map((token) => ({ Authorization: token }))),
)

function getAPI(url: string) {
    return process.env.REACT_APP_API_BASE + url
}

export function get(url: string) {
    return authHeader$.pipe(
        switchMap((header) => ajax.get(getAPI(url), header)),
    )
}

export function post(url: string, data?: any) {
    return authHeader$.pipe(
        switchMap((header) =>
            ajax.post(getAPI(url), JSON.stringify(data), header),
        ),
    )
}
function objectToFormData(data: { [key: string]: any }): FormData {
    const formData = new FormData()
    Object.keys(data).forEach((k) => formData.append(k, data[k]))
    return formData
}

export function put(url: string, data?: any) {
    return authHeader$.pipe(
        switchMap((header) =>
            ajax.put(getAPI(url), JSON.stringify(data), header),
        ),
    )
}

export function head(url: string) {
    return authHeader$.pipe(
        switchMap((header) =>
            ajax({
                crossDomain: true,
                headers: header,
                method: "HEAD",
                url: getAPI(url),
            }),
        ),
    )
}

// post form. convert given object into formdata
export function postForm(url: string, data: { [key: string]: any }) {
    return authHeader$.pipe(
        switchMap((header) => {
            return ajax.post(getAPI(url), objectToFormData(data), header)
        }),
    )
}

export function httpDelete(url: string) {
    return authHeader$.pipe(
        switchMap((header) => ajax.delete(getAPI(url), header)),
    )
}

export interface Progress {
    loaded: number
    total: number
}

export function upload(
    urlLink: GetUploadLinksResponseInfo,
    file: File,
): Observable<Progress> {
    return ajax({
        body: file as File,
        crossDomain: true,
        method: "PUT",
        url: urlLink.getLink() as string,
        includeUploadProgress: true,
    }).pipe(
        filter((event) => event.type === "upload_progress"),
        map(({ loaded, total }) => {
            return { loaded, total }
        }),
    )
}
export function resendActivationEmail(email: string, turnstileToken: string) {
    return fetchPost({
        url: RESEND_ACTIVATION_EMAIL,
        data: {
            email,
            ValidationData: {
                "cf-turnstile-response": turnstileToken,
            },
        },
        defaultErrorMessage: "Failed to send verification email",
    })
}

async function fetchPost<T>({
    url,
    data,
    options = {},
    defaultErrorMessage = "",
}: {
    url: string
    data: T
    options?: any
    defaultErrorMessage?: string
}) {
    return fetch(getUrl(url), {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
            credentials: "include",
            "Content-Type": "application/json",
            ...(options.headers || {}),
        },
        ...options,
    }).then(async (res) => {
        if (!res.ok) {
            const contentType = res.headers.get("content-type")
            if (contentType && contentType.includes("application/json")) {
                const errorData = await res.json()
                throw new Error(errorData.message || defaultErrorMessage)
            } else {
                const text = await res.text()
                throw new Error(text || defaultErrorMessage)
            }
        }
        return res
    })
}
export function getUrl(url: string) {
    if (process.env.REACT_APP_API_BASE === "https://localhost") {
        return `http://localhost:8888${url}`
    }
    return url
}
